import { useState, useEffect, useCallback } from 'react';
import type { TerminalMessage, LogLevel } from '@/types';

interface UseTerminalSimulationOptions {
  messages: TerminalMessage[];
  onComplete?: () => void;
}

interface CurrentMessage {
  level: LogLevel;
  text: string;
  displayedText: string;
  isComplete: boolean;
}

export function useTerminalSimulation({ messages, onComplete }: UseTerminalSimulationOptions) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState<CurrentMessage | null>(null);
  const [allMessages, setAllMessages] = useState<Array<{ level: LogLevel; text: string }>>([]);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (currentMessageIndex >= messages.length) {
      setIsComplete(true);
      onComplete?.();
      return;
    }

    const message = messages[currentMessageIndex];
    const delay = currentMessageIndex === 0 ? message.delay : message.delay - (messages[currentMessageIndex - 1]?.delay || 0);

    const timer = setTimeout(() => {
      setCurrentMessage({
        level: message.level,
        text: message.text,
        displayedText: '',
        isComplete: false,
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [currentMessageIndex, messages, onComplete]);

  useEffect(() => {
    if (!currentMessage) return;

    if (currentMessage.displayedText.length < currentMessage.text.length) {
      const timer = setTimeout(() => {
        setCurrentMessage((prev) => {
          if (!prev) return null;
          const newDisplayedText = prev.text.slice(0, prev.displayedText.length + 1);
          return {
            ...prev,
            displayedText: newDisplayedText,
          };
        });
      }, 12); // ~12ms per character (4x faster)

      return () => clearTimeout(timer);
    } else {
      // Message complete, add to allMessages and move to next
      setAllMessages((prev) => [...prev, { level: currentMessage.level, text: currentMessage.text }]);
      setCurrentMessage(null);
      setCurrentMessageIndex((prev) => prev + 1);
    }
  }, [currentMessage]);

  const reset = useCallback(() => {
    setCurrentMessageIndex(0);
    setCurrentMessage(null);
    setAllMessages([]);
    setIsComplete(false);
  }, []);

  const skip = useCallback(() => {
    // Add all remaining messages immediately
    const remainingMessages = messages.slice(currentMessageIndex).map((msg) => ({
      level: msg.level,
      text: msg.text,
    }));
    
    setAllMessages((prev) => [...prev, ...remainingMessages]);
    setCurrentMessage(null);
    setCurrentMessageIndex(messages.length);
    setIsComplete(true);
    onComplete?.();
  }, [currentMessageIndex, messages, onComplete]);

  return {
    allMessages,
    currentMessage,
    isComplete,
    reset,
    skip,
  };
}
